import useSWR, { useSWRConfig, type Arguments } from "swr";
import useSWRMutation, { SWRMutationResponse } from "swr/mutation";
import {
  acknowledgeExperimentDiagnostic,
  acknowledgeExperimentDiagnosticDailySpend,
  createExperiment,
  createExperimentCampaign,
  createExperimentNote,
  deleteExperiment,
  deleteExperimentCampaign,
  deleteExperimentNote,
  fetch,
  getAnalysis,
  getAudienceAndCampaignOptions,
  getBusinessMetrics,
  getCampaignFilter,
  getClients,
  getEvidenceGatesCheck1,
  getEvidenceGatesCheck2,
  getExperiment,
  getExperimentAnalysisRun,
  getExperimentData,
  getExperimentDiagnostics,
  getExperimentTimeline,
  getExperimentTimelineSummary,
  getExperimentVertexRuns,
  getExperimentsDailyDelivery,
  getGlobalExperimentImport,
  getGlobalExperimentOptions,
  getJiraMetricsProjects,
  getJiraMetricsSummary,
  getJiraMetricsSyncRuns,
  getJiraMetricsTickets,
  getPerformanceData,
  getPredictionFlightOptions,
  getRevenueOverview,
  getStability,
  getTeamGoalsCPA,
  getTeamGoalsCPAClients,
  getTeamGoalsSummary,
  getUnreadNotificationCount,
  getUser,
  getVertexMonitoredModels,
  listExperimentAnalysisRuns,
  listExperimentCampaigns,
  listExperimentNotes,
  listExperimentTags,
  listExperimentVersions,
  listExperiments,
  listNotifications,
  listUsers,
  markAllNotificationsRead,
  markNotificationRead,
  runExperimentDiagnostics,
  scheduleExperimentAnalysisRun,
  setExperimentRollout,
  setExperimentRolloutDurability,
  setExperimentTags,
  updateExperiment,
  updateExperimentNote,
} from "./api";
import {
  AnalysisParams,
  AudienceOptionsParams,
  CPAClientsResponse,
  CPAResponse,
  CalibrationAnalysisParams,
  CalibrationAnalysisResponse,
  CalibrationPlotsResponse,
  CampaignFilterParams,
  CreateCampaignRequest,
  CreateExperimentRequest,
  CreateNoteRequest,
  EvidenceGatesCheck1Response,
  EvidenceGatesCheck2Response,
  Experiment,
  ExperimentAnalysisRunResponse,
  ExperimentAnalysisRunsResponse,
  ExperimentCampaign,
  ExperimentDataParams,
  ExperimentDataResponse,
  ExperimentDiagnosticsResponse,
  ExperimentDiagnosticsRunResponse,
  ExperimentNote,
  ExperimentTag,
  ExperimentTagsListParams,
  ExperimentTagsResponse,
  ExperimentTimelineParams,
  ExperimentTimelineResponse,
  ExperimentTimelineSummaryParams,
  ExperimentTimelineSummaryResponse,
  ExperimentVersionsResponse,
  ExperimentVertexRunsResponse,
  ExperimentsDailyDeliveryParams,
  ExperimentsDailyDeliveryResponse,
  ExperimentsListParams,
  ExperimentsListResponse,
  GlobalExperimentImportResponse,
  GlobalExperimentOptionsResponse,
  JiraMetricsProjectsResponse,
  JiraMetricsSummaryParams,
  JiraMetricsSummaryResponse,
  JiraMetricsSyncRunsResponse,
  JiraMetricsTicketParams,
  JiraMetricsTicketsResponse,
  NotificationsResponse,
  PerformanceParams,
  PredictionFlightsResponse,
  ScheduleExperimentAnalysisRequest,
  SetExperimentRolloutDurabilityRequest,
  SetExperimentRolloutRequest,
  StabilityResponse,
  TeamGoalsSummaryResponse,
  UpdateExperimentRequest,
  UpdateNoteRequest,
  User,
  VertexMonitoredModelsResponse,
} from "./api-types";

const EXPERIMENTS_LIST_KEY = "/api/experiments/list";
const EXPERIMENT_TAGS_KEY = "/api/experiment-tags";
// Enough rows to fill the picker's dropdown without paging it.
const experimentTagSuggestionLimit = 20;

function isExperimentTagsKey(key?: Arguments): boolean {
  return Array.isArray(key) && key[0] === EXPERIMENT_TAGS_KEY;
}

// useExperiments caches under the array key [EXPERIMENTS_LIST_KEY, params], so
// mutate(EXPERIMENTS_LIST_KEY) writes to a cache entry no component reads and the list
// keeps rendering stale rows. It also disables revalidateIfStale/OnFocus/OnReconnect,
// so a missed invalidation never repairs itself — only a hard refresh does.
//
// Matching on the array's first element instead reaches every cached page of the list
// whatever filters or pagination it was fetched with.
function isExperimentsListKey(key?: Arguments): boolean {
  return Array.isArray(key) && key[0] === EXPERIMENTS_LIST_KEY;
}

type BiddingEfficiencyParams = {
  start_date?: string;
  end_date?: string;
  audience: number;
  campaign: number;
  campaign_type: string;
};

function useBiddingEfficiency(params: BiddingEfficiencyParams) {
  const { trigger, isMutating, data, error } = useSWRMutation(
    ["/api/bidding_efficiency", params],
    async ([url, params]) => {
      try {
        return await fetch({ method: "post", url, data: params });
      } catch (err) {
        // Assuming err has response property (like Axios errors)
        if (err.response && err.response.data) {
          // Add the response data to the error object
          err.serverError = err.response.data;
        }
        throw err; // Re-throw to let SWR handle it
      }
    },
    { revalidate: false },
  );

  return {
    trigger,
    isMutating,
    data,
    error,
  };
}

function useCalibrationAnalysis(params: CalibrationAnalysisParams): SWRMutationResponse<CalibrationAnalysisResponse> {
  return useSWRMutation(
    ["/api/calibration_analysis", params],
    async ([url, params]) => {
      try {
        return await fetch({ method: "post", url, data: params });
      } catch (err) {
        // Assuming err has response property (like Axios errors)
        if (err.response && err.response.data) {
          // Add the response data to the error object
          err.serverError = err.response.data;
        }
        throw err; // Re-throw to let SWR handle it
      }
    },
    { revalidate: false },
  );
}

function useCalibrationPlots(params: CalibrationAnalysisParams): SWRMutationResponse<CalibrationPlotsResponse> {
  return useSWRMutation(
    ["/api/calibration_plots", params],
    async ([url, params]) => {
      try {
        return await fetch({ method: "post", url, data: params });
      } catch (err) {
        // Assuming err has response property (like Axios errors)
        if (err.response && err.response.data) {
          // Add the response data to the error object
          err.serverError = err.response.data;
        }
        throw err; // Re-throw to let SWR handle it
      }
    },
    { revalidate: false },
  );
}

function useAudienceAndCampaignOptions(params: AudienceOptionsParams = { campaign_type: "0" }) {
  if (!params.campaign_type) params.campaign_type = "0";
  const { data, error, isLoading } = useSWR(
    ["/api/audience_campaign_options", params],
    ([url, params]) => getAudienceAndCampaignOptions(url, params),
    { revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false },
  );
  return { data, isLoading, error };
}

function usePerformance(params: PerformanceParams, shouldFetch: boolean) {
  const { data, error, isLoading } = useSWR(
    shouldFetch ? ["/api/performance", params] : null,
    ([url, params]) => getPerformanceData(url, params),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useExperimentData(params: ExperimentDataParams, shouldFetch: boolean) {
  const { data, error, isLoading } = useSWR<ExperimentDataResponse>(
    shouldFetch ? ["/api/experiments", params] : null,
    ([url, params]: [string, ExperimentDataParams]) => getExperimentData(url, params),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useExperimentsDailyDelivery(params: ExperimentsDailyDeliveryParams | null, shouldFetch: boolean) {
  const { data, error, isLoading } = useSWR<ExperimentsDailyDeliveryResponse>(
    shouldFetch && params ? ["/api/druid/experiments_daily_delivery", params] : null,
    ([url, queryParams]: [string, ExperimentsDailyDeliveryParams]) => getExperimentsDailyDelivery(url, queryParams),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useExperimentTimelineSummary(params: ExperimentTimelineSummaryParams, shouldFetch: boolean) {
  const { data, error, isLoading } = useSWR<ExperimentTimelineSummaryResponse>(
    shouldFetch ? ["/api/experiments/timeline/summary", params] : null,
    ([url, queryParams]: [string, ExperimentTimelineSummaryParams]) => getExperimentTimelineSummary(url, queryParams),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useExperimentTimeline(params: ExperimentTimelineParams = {}, shouldFetch = true) {
  const { data, error, isLoading, mutate } = useSWR<ExperimentTimelineResponse>(
    shouldFetch ? ["/api/experiments/experiment-timeline", params] : null,
    ([url, queryParams]: [string, ExperimentTimelineParams]) => getExperimentTimeline(url, queryParams),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error, mutate };
}

function useExperiments(params: ExperimentsListParams) {
  const { data, error, isLoading, isValidating, mutate } = useSWR<ExperimentsListResponse>(
    [EXPERIMENTS_LIST_KEY, params],
    ([url, queryParams]: [string, ExperimentsListParams]) => listExperiments(url, queryParams),
    {
      keepPreviousData: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, isValidating, error, mutate };
}

function useExperiment(id: number | null) {
  const { data, error, isLoading, mutate } = useSWR<Experiment>(id ? `/api/experiments/${id}` : null, getExperiment, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  return { data, isLoading, error, mutate };
}

function useExperimentVersions(id: number | null) {
  const { data, error, isLoading, mutate } = useSWR<ExperimentVersionsResponse>(
    id ? `/api/experiments/${id}/versions` : null,
    listExperimentVersions,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: false,
    },
  );
  return { data: data?.data, isLoading, error, mutate };
}

function useExperimentDiagnostics(id: number | null) {
  const { data, error, isLoading, mutate } = useSWR<ExperimentDiagnosticsResponse>(
    id ? `/api/experiment-diagnostics/experiments/${id}` : null,
    getExperimentDiagnostics,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error, mutate };
}

function useStability() {
  const { data, error, isLoading, mutate } = useSWR<StabilityResponse>("/api/stability/models", getStability, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  return { data: data?.data, isLoading, error, mutate };
}

function useExperimentVertexRuns(id: number | null) {
  const { data, error, isLoading, mutate } = useSWR<ExperimentVertexRunsResponse>(
    id ? `/api/experiments/${id}/vertex-runs` : null,
    getExperimentVertexRuns,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data: data?.data, isLoading, error, mutate };
}

function useVertexMonitoredModels() {
  const { data, error, isLoading } = useSWR<VertexMonitoredModelsResponse>(
    "/api/vertex-monitor/models",
    getVertexMonitoredModels,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { models: data?.data.models ?? [], isLoading, error };
}

function useAcknowledgeExperimentDiagnostic(
  experimentId: number | null,
): SWRMutationResponse<{ data: string }, unknown, string, { metricId: number }> {
  const { mutate } = useSWRConfig();

  return useSWRMutation(
    "/api/experiment-diagnostics/metrics",
    async (_, { arg }: { arg: { metricId: number } }) => {
      return await acknowledgeExperimentDiagnostic(`/api/experiment-diagnostics/metrics/${arg.metricId}/acknowledge`);
    },
    {
      onSuccess: async () => {
        if (experimentId) {
          await mutate(`/api/experiment-diagnostics/experiments/${experimentId}`);
          await mutate(`/api/experiments/${experimentId}`);
        }
        await mutate(isExperimentsListKey);
      },
    },
  );
}

function useAcknowledgeExperimentDiagnosticDailySpend(
  experimentId: number | null,
): SWRMutationResponse<{ data: string }, unknown, string, { dailySpendId: number }> {
  const { mutate } = useSWRConfig();

  return useSWRMutation(
    "/api/experiment-diagnostics/daily-spends",
    async (_, { arg }: { arg: { dailySpendId: number } }) => {
      return await acknowledgeExperimentDiagnosticDailySpend(
        `/api/experiment-diagnostics/daily-spends/${arg.dailySpendId}/acknowledge`,
      );
    },
    {
      onSuccess: async () => {
        if (experimentId) {
          await mutate(`/api/experiment-diagnostics/experiments/${experimentId}`);
          await mutate(`/api/experiments/${experimentId}`);
        }
        await mutate(isExperimentsListKey);
      },
    },
  );
}

function useRunExperimentDiagnostics(
  experimentId: number | null,
): SWRMutationResponse<ExperimentDiagnosticsRunResponse, unknown, string, never> {
  const { mutate } = useSWRConfig();

  return useSWRMutation(
    `/api/experiment-diagnostics/experiments/${experimentId}/run`,
    async () => {
      if (!experimentId) {
        throw new Error("experiment id is required");
      }

      return await runExperimentDiagnostics(`/api/experiment-diagnostics/experiments/${experimentId}/run`);
    },
    {
      onSuccess: async () => {
        if (experimentId) {
          await mutate(`/api/experiment-diagnostics/experiments/${experimentId}`);
          await mutate(`/api/experiments/${experimentId}`);
        }
        await mutate(isExperimentsListKey);
      },
    },
  );
}

function useScheduleExperimentAnalysisRun(
  experimentId: number | null,
): SWRMutationResponse<ExperimentAnalysisRunResponse, unknown, string, ScheduleExperimentAnalysisRequest> {
  const { mutate } = useSWRConfig();

  return useSWRMutation(
    `/api/experiments/${experimentId ?? 0}/analysis-runs`,
    async (url, { arg }: { arg: ScheduleExperimentAnalysisRequest }) => {
      if (!experimentId) {
        throw new Error("experiment id is required");
      }
      return await scheduleExperimentAnalysisRun(url, arg);
    },
    {
      onSuccess: async data => {
        if (experimentId && data?.data?.flow_run_id) {
          await mutate(`/api/experiments/${experimentId}/analysis-runs/${data.data.flow_run_id}`);
        }
      },
    },
  );
}

function useExperimentAnalysisRunStatus(experimentId: number | null, flowRunId: string | null) {
  const { data, error, isLoading, isValidating, mutate } = useSWR<ExperimentAnalysisRunResponse>(
    experimentId && flowRunId ? `/api/experiments/${experimentId}/analysis-runs/${flowRunId}` : null,
    getExperimentAnalysisRun,
    {
      refreshInterval: latest => (latest?.data?.terminal ? 0 : 5000),
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return { data, isLoading, isValidating, error, mutate };
}

function useExperimentAnalysisRuns(experimentId: number | null, limit = 10) {
  const { data, error, isLoading, mutate } = useSWR<ExperimentAnalysisRunsResponse>(
    experimentId ? [`/api/experiments/${experimentId}/analysis-runs`, { limit }] : null,
    ([url, params]: [string, { limit: number }]) => listExperimentAnalysisRuns(`${url}?limit=${params.limit}`),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return { data: data?.data, isLoading, error, mutate };
}

function useCreateExperiment(): SWRMutationResponse<Experiment, unknown, string, CreateExperimentRequest> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    "/api/experiments",
    async (url, { arg }: { arg: CreateExperimentRequest }) => {
      return await createExperiment(url, arg);
    },
    {
      onSuccess: async () => {
        // Force the next experiments list mount to refetch, even if it is currently unmounted.
        await mutate(isExperimentsListKey, undefined, { revalidate: false });
      },
    },
  );
}

function useUpdateExperiment(id: number): SWRMutationResponse<Experiment, unknown, string, UpdateExperimentRequest> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/experiments/${id}`,
    async (url, { arg }: { arg: UpdateExperimentRequest }) => {
      return await updateExperiment(url, arg);
    },
    {
      onSuccess: async updatedExperiment => {
        await mutate(`/api/experiments/${id}`, updatedExperiment, { revalidate: false });
        await mutate(
          isExperimentsListKey,
          (current?: ExperimentsListResponse) =>
            current
              ? {
                  ...current,
                  data: current.data.map(experiment => (experiment.id === id ? updatedExperiment : experiment)),
                }
              : current,
          { revalidate: false },
        );
        await mutate(`/api/experiments/${id}/campaigns`);
        await mutate(`/api/experiments/${id}/versions`);
      },
    },
  );
}

function useSetExperimentRollout(
  id: number,
): SWRMutationResponse<Experiment, unknown, string, SetExperimentRolloutRequest> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/experiments/${id}/rollout`,
    async (url, { arg }: { arg: SetExperimentRolloutRequest }) => {
      return await setExperimentRollout(url, arg);
    },
    {
      onSuccess: async updatedExperiment => {
        await mutate(`/api/experiments/${id}`, updatedExperiment, { revalidate: false });
        await mutate(
          isExperimentsListKey,
          (current?: ExperimentsListResponse) =>
            current
              ? {
                  ...current,
                  data: current.data.map(experiment => (experiment.id === id ? updatedExperiment : experiment)),
                }
              : current,
          { revalidate: false },
        );
        await mutate(`/api/experiments/${id}/versions`);
      },
    },
  );
}

function useSetExperimentRolloutDurability(
  id: number,
): SWRMutationResponse<Experiment, unknown, string, SetExperimentRolloutDurabilityRequest> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/experiments/${id}/rollout-durability`,
    async (url, { arg }: { arg: SetExperimentRolloutDurabilityRequest }) => {
      return await setExperimentRolloutDurability(url, arg);
    },
    {
      onSuccess: async updatedExperiment => {
        await mutate(`/api/experiments/${id}`, updatedExperiment, { revalidate: false });
        await mutate(
          isExperimentsListKey,
          (current?: ExperimentsListResponse) =>
            current
              ? {
                  ...current,
                  data: current.data.map(experiment => (experiment.id === id ? updatedExperiment : experiment)),
                }
              : current,
          { revalidate: false },
        );
        await mutate(`/api/experiments/${id}/versions`);
      },
    },
  );
}

function useDeleteExperiment(id: number): SWRMutationResponse<{ data: string }, unknown, string, void> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/experiments/${id}`,
    async url => {
      return await deleteExperiment(url);
    },
    {
      onSuccess: () => {
        mutate("/api/experiments/list");
      },
    },
  );
}

// Users hook
function useUsers() {
  const { data, error, isLoading } = useSWR<{ data: User[] }>("/api/users", listUsers, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  return { data: data?.data, isLoading, error };
}

// Experiment Campaigns hooks
function useExperimentCampaigns(experimentId: number | null) {
  const { data, error, isLoading, mutate } = useSWR<{ data: ExperimentCampaign[] }>(
    experimentId ? `/api/experiments/${experimentId}/campaigns` : null,
    listExperimentCampaigns,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data: data?.data, isLoading, error, mutate };
}

function useCreateExperimentCampaign(
  experimentId: number,
): SWRMutationResponse<ExperimentCampaign, unknown, string, CreateCampaignRequest> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/experiments/${experimentId}/campaigns`,
    async (url, { arg }: { arg: CreateCampaignRequest }) => {
      return await createExperimentCampaign(url, arg);
    },
    {
      onSuccess: () => {
        mutate(`/api/experiments/${experimentId}/campaigns`);
        mutate(`/api/experiments/${experimentId}`);
      },
    },
  );
}

function useDeleteExperimentCampaign(
  experimentId: number,
): SWRMutationResponse<{ data: string }, unknown, string, number> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/experiment-campaigns`,
    async (_, { arg: campaignId }: { arg: number }) => {
      return await deleteExperimentCampaign(`/api/experiment-campaigns/${campaignId}`);
    },
    {
      onSuccess: () => {
        mutate(`/api/experiments/${experimentId}/campaigns`);
        mutate(`/api/experiments/${experimentId}`);
      },
    },
  );
}

// Experiment Notes hooks
function useExperimentNotes(experimentId: number | null) {
  const { data, error, isLoading, mutate } = useSWR<{ data: ExperimentNote[] }>(
    experimentId ? `/api/experiments/${experimentId}/notes` : null,
    listExperimentNotes,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data: data?.data, isLoading, error, mutate };
}

function useCreateExperimentNote(
  experimentId: number,
): SWRMutationResponse<ExperimentNote, unknown, string, CreateNoteRequest> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/experiments/${experimentId}/notes`,
    async (url, { arg }: { arg: CreateNoteRequest }) => {
      return await createExperimentNote(url, arg);
    },
    {
      onSuccess: () => {
        mutate(`/api/experiments/${experimentId}/notes`);
      },
    },
  );
}

function useUpdateExperimentNote(
  experimentId: number,
): SWRMutationResponse<ExperimentNote, unknown, string, { noteId: number; data: UpdateNoteRequest }> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/notes`,
    async (_, { arg }: { arg: { noteId: number; data: UpdateNoteRequest } }) => {
      return await updateExperimentNote(`/api/notes/${arg.noteId}`, arg.data);
    },
    {
      onSuccess: () => {
        mutate(`/api/experiments/${experimentId}/notes`);
      },
    },
  );
}

function useDeleteExperimentNote(experimentId: number): SWRMutationResponse<{ data: string }, unknown, string, number> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/notes`,
    async (_, { arg: noteId }: { arg: number }) => {
      return await deleteExperimentNote(`/api/notes/${noteId}`);
    },
    {
      onSuccess: () => {
        mutate(`/api/experiments/${experimentId}/notes`);
      },
    },
  );
}

// Experiment Tags hooks
//
// An empty search asks the backend for the most recently used tags, which is what the
// picker offers before the user types. Callers are expected to debounce `search` and to
// hold it empty below the minimum query length, so typing does not fire a request per
// keystroke.
function useExperimentTags(search: string, shouldFetch = true) {
  const { data, error, isLoading, isValidating, mutate } = useSWR<ExperimentTagsResponse>(
    shouldFetch ? [EXPERIMENT_TAGS_KEY, { search: search || undefined, limit: experimentTagSuggestionLimit }] : null,
    ([url, queryParams]: [string, ExperimentTagsListParams]) => listExperimentTags(url, queryParams),
    {
      keepPreviousData: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data: data?.data, isLoading, isValidating, error, mutate };
}

/**
 * Writes an experiment's new tags into a cached page of the experiments list.
 *
 * A bare `mutate(isExperimentsListKey)` is not enough: tags are edited on the detail page
 * while the list is unmounted, so there is no revalidator registered to answer it, and
 * the list turns off revalidateIfStale/OnFocus/OnReconnect — the stale rows would survive
 * the trip back and only a hard refresh would repair them.
 */
function withExperimentTags(
  current: ExperimentsListResponse | undefined,
  experimentId: number,
  tags: ExperimentTag[],
): ExperimentsListResponse | undefined {
  if (!current) {
    return current;
  }

  return {
    ...current,
    data: current.data.map(experiment => (experiment.id === experimentId ? { ...experiment, tags } : experiment)),
  };
}

function useSetExperimentTags(
  experimentId: number,
): SWRMutationResponse<ExperimentTagsResponse, unknown, string, string[]> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    `/api/experiments/${experimentId}/tags`,
    async (url, { arg }: { arg: string[] }) => {
      return await setExperimentTags(url, { tags: arg });
    },
    {
      onSuccess: async response => {
        // Patch so the change lands instantly, and revalidate because tags decide list
        // membership: dropping the tag the list is filtered by has to remove the row and
        // shrink the total, which no local patch can work out. Only a mounted list has a
        // revalidator, so when this runs from the detail page the patch is what survives
        // the trip back — which is the whole point of patching rather than invalidating.
        await mutate(
          isExperimentsListKey,
          (current?: ExperimentsListResponse) => withExperimentTags(current, experimentId, response.data),
          { revalidate: true },
        );
        // A save can mint a brand new tag and always shifts which tags rank as most
        // recent, and neither can be patched in place, so drop the catalogue instead:
        // an open picker refetches now, a closed one refetches when it next opens.
        await mutate(isExperimentTagsKey, undefined, { revalidate: true });
      },
    },
  );
}

function useOverview() {
  const { data, error, isLoading } = useSWR("/api/overview", getRevenueOverview, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return { data, isLoading, error };
}

function useClients() {
  const { data, error, isLoading } = useSWR("/api/clients", getClients, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return { data, isLoading, error };
}

function useAnalysis(params: AnalysisParams, shouldFetch: boolean) {
  const { data, error, isLoading } = useSWR(
    shouldFetch ? ["/api/analysis", params] : null,
    ([url, params]) => getAnalysis(url, params),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useUser() {
  const { data, error, isLoading } = useSWR("/api/user", getUser, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  return { data, isLoading, error };
}

function useCampaignFilter(params: CampaignFilterParams, shouldFetch: boolean) {
  const { data, error, isLoading } = useSWR(
    shouldFetch ? ["/api/druid/ua_campaign_filter", params] : null,
    ([url, params]) => getCampaignFilter(url, params),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useBusinessMetrics() {
  const { data, error, isLoading } = useSWR("/api/business", getBusinessMetrics, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  return { data, isLoading, error };
}

function usePredictionFlightOptions() {
  const { data, error, isLoading } = useSWR<PredictionFlightsResponse>(
    "/api/prediction_flight_options",
    getPredictionFlightOptions,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useGlobalExperimentOptions() {
  const { data, error, isLoading } = useSWR<GlobalExperimentOptionsResponse>(
    "/api/global_experiment_options",
    getGlobalExperimentOptions,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useImportGlobalExperiment(): SWRMutationResponse<GlobalExperimentImportResponse, unknown, string, number> {
  return useSWRMutation(
    "/api/global_experiment_import",
    async (_, { arg: globalExperimentId }: { arg: number }) => {
      return await getGlobalExperimentImport(`/api/global_experiments/${globalExperimentId}/import`);
    },
    { revalidate: false },
  );
}

function useJiraMetricsProjects() {
  const { data, error, isLoading } = useSWR<JiraMetricsProjectsResponse>(
    "/api/jira-metrics/projects",
    getJiraMetricsProjects,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data: data?.data, isLoading, error };
}

function useJiraMetricsSummary(params: JiraMetricsSummaryParams, shouldFetch: boolean) {
  const { data, error, isLoading } = useSWR<JiraMetricsSummaryResponse>(
    shouldFetch ? ["/api/jira-metrics/summary", params] : null,
    ([url, summaryParams]: [string, JiraMetricsSummaryParams]) => getJiraMetricsSummary(url, summaryParams),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useJiraMetricsTickets(params: JiraMetricsTicketParams, shouldFetch: boolean) {
  const { data, error, isLoading, isValidating, mutate } = useSWR<JiraMetricsTicketsResponse>(
    shouldFetch ? ["/api/jira-metrics/tickets", params] : null,
    ([url, ticketParams]: [string, JiraMetricsTicketParams]) => getJiraMetricsTickets(url, ticketParams),
    {
      keepPreviousData: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, isValidating, mutate, error };
}

function useJiraMetricsSyncRuns(limit = 10) {
  const { data, error, isLoading } = useSWR<JiraMetricsSyncRunsResponse>(
    ["/api/jira-metrics/sync-runs", { limit }],
    ([url, queryParams]: [string, { limit?: number }]) => getJiraMetricsSyncRuns(url, queryParams),
    {
      refreshInterval: 60 * 1000,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useEvidenceGatesCheck1() {
  const { data, error, isLoading } = useSWR<EvidenceGatesCheck1Response>(
    "/api/evidence-gates/check-1",
    getEvidenceGatesCheck1,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useEvidenceGatesCheck2() {
  const { data, error, isLoading } = useSWR<EvidenceGatesCheck2Response>(
    "/api/evidence-gates/check-2",
    getEvidenceGatesCheck2,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useTeamGoalsSummary() {
  const { data, error, isLoading } = useSWR<TeamGoalsSummaryResponse>("/api/team-goals/summary", getTeamGoalsSummary, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  return { data, isLoading, error };
}

function useTeamGoalsCPAClients() {
  const { data, error, isLoading } = useSWR<CPAClientsResponse>("/api/team-goals/cpa-clients", getTeamGoalsCPAClients, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  return { data, isLoading, error };
}

function useTeamGoalsCPA(orgIds: number[], shouldFetch: boolean) {
  const orgIdsParam = orgIds.join(",");
  const { data, error, isLoading } = useSWR<CPAResponse>(
    shouldFetch && orgIds.length > 0 ? ["/api/team-goals/cpa", orgIdsParam] : null,
    ([url]: [string]) => getTeamGoalsCPA(url, { org_ids: orgIdsParam }),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data, isLoading, error };
}

function useNotifications(params: { unreadOnly: boolean; limit: number }) {
  const key = `/api/notifications?unread_only=${params.unreadOnly}&limit=${params.limit}`;
  const { data, error, isLoading, mutate } = useSWR<NotificationsResponse>(key, listNotifications, {
    refreshInterval: 30_000,
    revalidateOnFocus: true,
  });
  return { data, isLoading, error, mutate };
}

function useUnreadNotificationCount() {
  const { data, error } = useSWR<{ unread_count: number }>("/api/notifications/unread", getUnreadNotificationCount, {
    refreshInterval: 30_000,
    revalidateOnFocus: true,
  });
  return { unreadCount: data?.unread_count ?? 0, error };
}

function invalidateNotifications(mutate: ReturnType<typeof useSWRConfig>["mutate"]) {
  mutate(key => typeof key === "string" && key.startsWith("/api/notifications"));
}

function useMarkNotificationRead(): SWRMutationResponse<{ data: string }, unknown, string, number> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    "/api/notifications/mark-read",
    async (_, { arg: id }: { arg: number }) => {
      return await markNotificationRead(`/api/notifications/${id}/read`);
    },
    {
      onSuccess: () => {
        invalidateNotifications(mutate);
      },
    },
  );
}

function useMarkAllNotificationsRead(): SWRMutationResponse<{ data: string }, unknown, string, void> {
  const { mutate } = useSWRConfig();
  return useSWRMutation(
    "/api/notifications/read-all",
    async url => {
      return await markAllNotificationsRead(url);
    },
    {
      onSuccess: () => {
        invalidateNotifications(mutate);
      },
    },
  );
}

export {
  EXPERIMENTS_LIST_KEY,
  // Exported for the regression tests that pin the cache key shapes and the list patch.
  EXPERIMENT_TAGS_KEY,
  isExperimentTagsKey,
  isExperimentsListKey,
  useAcknowledgeExperimentDiagnostic,
  useAcknowledgeExperimentDiagnosticDailySpend,
  useAnalysis,
  useAudienceAndCampaignOptions,
  useBiddingEfficiency,
  useBusinessMetrics,
  useCalibrationAnalysis,
  useCalibrationPlots,
  useCampaignFilter,
  useClients,
  useCreateExperiment,
  useCreateExperimentCampaign,
  useCreateExperimentNote,
  useDeleteExperiment,
  useDeleteExperimentCampaign,
  useDeleteExperimentNote,
  useEvidenceGatesCheck1,
  useEvidenceGatesCheck2,
  useExperiment,
  useExperimentAnalysisRunStatus,
  useExperimentAnalysisRuns,
  useExperimentCampaigns,
  useExperimentData,
  useExperimentDiagnostics,
  useExperimentNotes,
  useExperimentTags,
  useExperimentTimeline,
  useExperimentTimelineSummary,
  useExperimentVersions,
  useExperimentVertexRuns,
  useExperiments,
  useExperimentsDailyDelivery,
  useGlobalExperimentOptions,
  useImportGlobalExperiment,
  useJiraMetricsProjects,
  useJiraMetricsSummary,
  useJiraMetricsSyncRuns,
  useJiraMetricsTickets,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useOverview,
  usePerformance,
  usePredictionFlightOptions,
  useRunExperimentDiagnostics,
  useScheduleExperimentAnalysisRun,
  useSetExperimentRollout,
  useSetExperimentRolloutDurability,
  useSetExperimentTags,
  useStability,
  useTeamGoalsCPA,
  useTeamGoalsCPAClients,
  useTeamGoalsSummary,
  useUnreadNotificationCount,
  useUpdateExperiment,
  useUpdateExperimentNote,
  useUser,
  useUsers,
  useVertexMonitoredModels,
  withExperimentTags,
};
